import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * U15 F4: "Ler em voz alta" com a voz do próprio navegador (Web Speech API, `speechSynthesis`): sem custo e sem serviço
 * externo. Usado no painel de ajuda da equipe e no do Portal. Sem suporte no navegador, `suportado` é falso e a tela não
 * mostra o botão. A voz acompanha o idioma da pessoa (pt-BR, en-US, es-ES).
 */
const LINGUAS: Record<string, string> = { pt: 'pt-BR', en: 'en-US', es: 'es-ES' };

export const linguaDaVoz = (idioma: string | undefined) => LINGUAS[(idioma ?? 'pt').slice(0, 2)] ?? 'pt-BR';

export function leituraSuportada(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window && typeof window.SpeechSynthesisUtterance !== 'undefined';
}

/** Voz instalada que combina com a língua (a do mesmo país primeiro, depois qualquer uma da mesma língua); sem nenhuma, o navegador escolhe. */
function escolherVoz(lingua: string): SpeechSynthesisVoice | undefined {
  const vozes = window.speechSynthesis.getVoices?.() ?? [];
  const alvo = lingua.toLowerCase();
  return vozes.find((v) => v.lang.toLowerCase().replace('_', '-') === alvo) ?? vozes.find((v) => v.lang.toLowerCase().startsWith(alvo.slice(0, 2)));
}

export function useLeituraEmVoz(idioma: string | undefined) {
  const suportado = leituraSuportada();
  const [lendo, setLendo] = useState(false);
  // A leitura em curso: o evento de fim de uma leitura já cancelada não pode apagar o estado da seguinte.
  const atual = useRef<SpeechSynthesisUtterance | null>(null);
  const parar = useCallback(() => {
    atual.current = null;
    if (leituraSuportada()) window.speechSynthesis.cancel();
    setLendo(false);
  }, []);
  const ler = useCallback(
    (texto: string) => {
      if (!leituraSuportada() || !texto.trim()) return;
      window.speechSynthesis.cancel();
      const fala = new window.SpeechSynthesisUtterance(texto);
      const lingua = linguaDaVoz(idioma);
      fala.lang = lingua;
      const voz = escolherVoz(lingua);
      if (voz) fala.voice = voz;
      const fim = () => {
        if (atual.current === fala) {
          atual.current = null;
          setLendo(false);
        }
      };
      fala.onend = fim;
      fala.onerror = fim;
      atual.current = fala;
      setLendo(true);
      window.speechSynthesis.speak(fala);
    },
    [idioma],
  );
  // Fechar o painel (desmontar) para a leitura.
  useEffect(
    () => () => {
      if (leituraSuportada()) window.speechSynthesis.cancel();
    },
    [],
  );
  return { suportado, lendo, ler, parar };
}
